import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AuthLayout from '../../components/layout/AuthLayout';
import BaseDropdown from '../../components/form/BaseDropdown';
import BaseButton from '../../components/form/BaseButton';
import WorldMap from '../../components/misc/global-view/WorldMap.jsx';
import ContinentMap from '../../components/misc/global-view/ContinentMap.jsx';
import CountryMap from '../../components/misc/global-view/CountryMap.jsx';
import { useLazyGetGlobalViewQuery } from '../../api/global-view.js';
import { useGetCountriesQuery } from '../../api/misc.js';
import StatsPerMap from '../../components/misc/global-view/StatsPerMap.jsx';
import { useNavigate } from 'react-router-dom';

const normalizeContinent = (country) => {
    if (country.region.continent.includes('/')) {
        const override = {
            russia: 'Europe',
            turkey: 'Asia',
            kazakhstan: 'Asia',
            azerbaijan: 'Europe'
        };
        const nameKey = country.name.toLowerCase();
        if (override[nameKey]) {
            return override[nameKey];
        }

        return country.region.continent.split('/')[0];
    }
    return country.region.continent;
};

const GlobalView = () => {
    const navigate = useNavigate();
    const [view, setView] = useState('world');
    const [selectedContinent, setSelectedContinent] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState(null);

    const [getGlobalView, { data: globalData, isFetching }] = useLazyGetGlobalViewQuery();

    const { data: countriesData } = useGetCountriesQuery();
    const { options: countryOptions, countryLookup } = useMemo(() => {
        let options = [];
        let lookup = {};
        if (countriesData?.response?.countries) {
            countriesData.response.countries.forEach((country) => {
                options.push({
                    label: country.name,
                    value: country
                });
                lookup[country.name.toLowerCase()] = country;
            });
        }
        return { options, countryLookup: lookup };
    }, [countriesData]);

    const fetchStats = useCallback(
        (params = {}) => {
            getGlobalView({ ...params });
        },
        [getGlobalView]
    );

    useEffect(() => {
        if (view === 'world') {
            fetchStats();
        }
    }, [view, fetchStats]);

    const handleContinentClick = (continent) => {
        setSelectedContinent(continent);
        setSelectedCountry(null);
        setView('continent');
        fetchStats({ continent });
    };

    const handleCountryClick = (country) => {
        let countryObj;
        if (typeof country === 'object' && country.region) {
            countryObj = country;
        } else if (typeof country === 'string') {
            countryObj = countryLookup[country.toLowerCase()];
        }
        if (!countryObj) {
            console.warn('Country not found:', country);
            return;
        }
        setSelectedCountry(countryObj);
        setSelectedContinent(normalizeContinent(countryObj));
        setView('country');
        fetchStats({ country: countryObj.id });
    };

    const goBackToGlobal = () => {
        setView('world');
        setSelectedContinent(null);
        setSelectedCountry(null);
        fetchStats();
    };

    const goBackToContinent = () => {
        setView('continent');
        setSelectedCountry(null);
        if (selectedContinent) {
            fetchStats({ continent: selectedContinent });
        }
    };

    const getTitle = () => {
        if (view === 'world') {
            return 'Global View';
        } else if (view === 'continent' && selectedContinent) {
            return `${selectedContinent} View`;
        } else if (view === 'country' && selectedCountry) {
            return `${selectedCountry.name} View`;
        }
        return 'Global View';
    };

    return (
        <AuthLayout title={getTitle()} backRoute='/dashboard'>
            <div className='max-w-3xl w-full mx-auto px-8 mt-6'>
                <div className='mb-6'>
                    <BaseDropdown
                        name='countryDropdown'
                        placeholder='Select a country'
                        options={countryOptions}
                        value={selectedCountry}
                        onChange={(option) => {
                            if (option && option.value) {
                                handleCountryClick(option.value);
                            }
                        }}
                    />
                    <div className='mt-2 text-right'>
                        <span
                            className='text-base font-bold cursor-pointer'
                            onClick={() => {
                                if (view === 'continent') {
                                    navigate('/global-view/advanced-search', {
                                        state: { preFilterContinent: selectedContinent }
                                    });
                                } else if (view === 'country') {
                                    navigate('/global-view/advanced-search', {
                                        state: { preFilterCountry: selectedCountry?.name }
                                    });
                                } else {
                                    navigate('/global-view/advanced-search');
                                }
                            }}
                        >
                            Advanced Search
                        </span>
                    </div>
                </div>

                {view !== 'world' && (
                    <div className='flex flex-col [@media(min-width:430px)]:flex-row gap-2 mb-4'>
                        <BaseButton
                            className='btn-link-primary !w-auto !text-base !p-2 !px-3 !font-medium md:!font-bold'
                            onClick={goBackToGlobal}
                            textValue='Back to Global View'
                        />
                        {view === 'country' && (
                            <BaseButton
                                className='btn-link-primary !w-auto !text-base !p-2 !px-3 !font-medium md:!font-bold'
                                onClick={goBackToContinent}
                                textValue={`Back to ${selectedContinent} View`}
                            />
                        )}
                    </div>
                )}
            </div>

            <div className='mt-4 max-w-3xl w-full mx-auto px-2'>
                <div style={{ height: '50vh' }}>
                    {view === 'world' && <WorldMap onContinentClick={handleContinentClick} />}
                    {view === 'continent' && selectedContinent && (
                        <ContinentMap
                            selectedContinent={selectedContinent}
                            onCountryClick={handleCountryClick}
                            countryLookup={countryLookup}
                        />
                    )}
                    {view === 'country' && selectedCountry && (
                        <CountryMap
                            key={selectedCountry.name}
                            selectedContinent={selectedContinent}
                            selectedCountry={selectedCountry.name}
                            goBackToContinent={goBackToContinent}
                        />
                    )}
                </div>
            </div>
            <div className='max-w-3xl w-full mx-auto px-8 mb-10'>
                <StatsPerMap data={globalData?.response[0]} />
            </div>
        </AuthLayout>
    );
};

export default GlobalView;
